import { Type, type Static } from "@sinclair/typebox";
import type { CampaignChannel } from "./campaigns";

// ----------------------------
// Automation Conditions (TypeBox — validated before DB storage)
// ----------------------------

export const AutomationPartySizeConditionSchema = Type.Object({
  type: Type.Literal("party_size"),
  operator: Type.Union([Type.Literal("gte"), Type.Literal("lte")]),
  value: Type.Integer({ minimum: 1 }),
});
export type AutomationPartySizeCondition = Static<typeof AutomationPartySizeConditionSchema>;

export const AutomationOrderTotalConditionSchema = Type.Object({
  type: Type.Literal("order_total"),
  operator: Type.Union([Type.Literal("gte"), Type.Literal("lte")]),
  value: Type.Number({ minimum: 0 }), // cents
});
export type AutomationOrderTotalCondition = Static<typeof AutomationOrderTotalConditionSchema>;

export const AutomationStatusLevelConditionSchema = Type.Object({
  type: Type.Literal("status_level"),
  value: Type.String({ minLength: 1 }),
});
export type AutomationStatusLevelCondition = Static<typeof AutomationStatusLevelConditionSchema>;

export const AutomationConditionSchema = Type.Union([
  AutomationPartySizeConditionSchema,
  AutomationOrderTotalConditionSchema,
  AutomationStatusLevelConditionSchema,
]);
export type AutomationCondition = Static<typeof AutomationConditionSchema>;

// ----------------------------
// Automation Message Template (TypeBox)
// ----------------------------

export const AutomationMessageTemplateSchema = Type.Object({
  subject: Type.Optional(Type.String()),
  body: Type.String(),
});
export type AutomationMessageTemplate = Static<typeof AutomationMessageTemplateSchema>;

// ----------------------------
// Automation
// ----------------------------

export type AutomationTriggerEvent =
  | "reservation_completed"
  | "reservation_created"
  | "order_completed"
  | "birthday"
  | "member_created";

// Available template variables per trigger event:
// reservation_completed / reservation_created:
//   {{customer_name}}, {{reservation_date}}, {{party_size}}, {{shop_name}}, {{review_link}}
// order_completed:
//   {{customer_name}}, {{order_total}}, {{shop_name}}
// birthday:
//   {{customer_name}}, {{shop_name}}
// member_created:
//   {{customer_name}}, {{program_name}}

export type Automation = {
  id: string;
  shop_id: number | null;
  business_unit_id: number;
  name: string;
  trigger_event: AutomationTriggerEvent;
  delay_minutes: number;         // durable sleep before sending; 0 = immediate
  channels: CampaignChannel[];
  message_template: AutomationMessageTemplate;
  conditions: AutomationCondition[]; // ANDed — all must pass
  review_link: string | null;    // used in {{review_link}} for reservation triggers
  // The membership program (list) a `member_created` automation targets; null
  // for every other trigger event.
  membership_program_id: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // serializer stats
  total_sends: number;
  total_sent: number;
};

// ----------------------------
// Automation Send
// ----------------------------

// One record per firing. Unique on (automation_id, trigger_source_id) for idempotency.
// The DBOS workflow inserts this in step 1; if ON CONFLICT it exits immediately.

export type AutomationSendStatus = "pending" | "sent" | "failed" | "skipped";

export type AutomationSend = {
  id: string;
  automation_id: string;
  customer_id: string;
  trigger_source_id: string;     // reservation.id / order.id / membership_members.id that fired it
  trigger_source_type: "reservation" | "order" | "birthday" | "membership_member";
  status: AutomationSendStatus;
  scheduled_for: string;         // created_at + delay_minutes
  sent_at: string | null;
  error: string | null;
  created_at: string;
};
